(() => {
  if (customElements.get('hs-diamond-hand')) return;

  const aliases = {
    RB: 'Round', ROUND: 'Round',
    OV: 'Oval', OVAL: 'Oval',
    PR: 'Princess', PRINCESS: 'Princess',
    PE: 'Pear', PO: 'Pear', PEAR: 'Pear',
    CU: 'Cushion', CUSQ: 'Cushion', CUSHION: 'Cushion', ELONGATED_CUSHION: 'Cushion',
    EM: 'Emerald', EMERALD: 'Emerald',
    RAD: 'Radiant', RADIANT: 'Radiant',
    MQ: 'Marquise', MARQUISE: 'Marquise',
    HT: 'Heart', HEART: 'Heart',
    ASH: 'Asscher', ASSHER: 'Asscher', ASSCHER: 'Asscher'
  };

  const paths = {
    Round: 'M50 1 A49 49 0 1 1 49.99 1Z',
    Oval: 'M50 1 A49 49 0 1 1 49.99 1Z',
    Princess: 'M1 1H99V99H1Z',
    Pear: 'M50 1C44 13 1 48 1 68C1 110 99 110 99 68C99 48 56 13 50 1Z',
    Cushion: 'M23 2Q50 -1 77 2Q97 3 98 23Q101 50 98 77Q97 97 77 98Q50 101 23 98Q3 97 2 77Q-1 50 2 23Q3 3 23 2Z',
    Emerald: 'M16 1H84L99 16V84L84 99H16L1 84V16Z',
    Radiant: 'M14 1H86L99 14V86L86 99H14L1 86V14Z',
    Marquise: 'M50 1Q148 50 50 99Q-48 50 50 1Z',
    Heart: 'M50 99C38 85 1 60 1 29C1 -5 35 -8 50 16C65 -8 99 -5 99 29C99 60 62 85 50 99Z',
    Asscher: 'M22 1H78L99 22V78L78 99H22L1 78V22Z'
  };

  const tones = [
    ['#fff3e8', 0.12],
    ['#deb48f', 0.22],
    ['#bc875e', 0.38],
    ['#965f3e', 0.52],
    ['#70442f', 0.66],
    ['#452a20', 0.78]
  ];
  const MAX = tones.length - 1;

  const hexToRgb = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const mixColor = (a, b, t) => {
    const A = hexToRgb(a), B = hexToRgb(b);
    return `rgb(${A.map((v, k) => Math.round(v + (B[k] - v) * t)).join(',')})`;
  };
  const clamp = n => Math.min(MAX, Math.max(0, n));

  customElements.define('hs-diamond-hand', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      // Read spec table from product description
      const doc = this.querySelector('template').content;
      const fields = {};
      doc.querySelectorAll('tr').forEach(r => {
        const k = r.querySelector('th'), v = r.querySelector('td');
        if (k && v) fields[k.textContent.trim().toLowerCase()] = v.textContent.trim();
      });

      const normalize = s => (s || '').trim().toUpperCase().replace(/[ -]+/g, '_');
      const shape = aliases[normalize(this.dataset.shape)] || aliases[normalize(fields.shape)];
      if (!shape) return;

      const dims = (fields.measurements || '').match(
        /^\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:mm)?\s*$/i
      );
      const valid = dims && dims.slice(1).every(n => Number(n) > 0 && Number(n) <= 100);
      if (!valid) return;

      // Size and position the stone
      const length = Number(dims[1]), width = Number(dims[2]), pxPerMm = 112 / 17;
      const gem = this.querySelector('.hs-diamond-hand__stone');
      gem.setAttribute('width', width * pxPerMm);
      gem.setAttribute('height', length * pxPerMm);
      gem.setAttribute('x', 380 - width * pxPerMm / 2);
      gem.setAttribute('y', 545 - length * pxPerMm / 2);

      // Draw facets
      const uid = 'hs-gem-' + this.querySelector('input').id;
      const outline = paths[shape];
      let facets = '';
      if (shape === 'Emerald' || shape === 'Asscher') {
        for (let i = 0; i < 4; i++) {
          const s = 1 - i * 0.17;
          facets += `<path d="${outline}" transform="translate(${50 * (1 - s)} ${50 * (1 - s)}) scale(${s})" fill="${['#dce4e8', '#74838c', '#f8fcff', '#b4c3cc'][i]}" stroke="#ffffff" stroke-width="1"/>`;
        }
      } else {
        for (let i = 0; i < 16; i++) {
          const a = i * Math.PI / 8, b = (i + 1) * Math.PI / 8;
          const x = 50 + 70 * Math.cos(a), y = 50 + 70 * Math.sin(a);
          const xx = 50 + 70 * Math.cos(b), yy = 50 + 70 * Math.sin(b);
          facets += `<path d="M50 50L${x} ${y}L${xx} ${yy}Z" fill="${['#faffff', '#a7b7c2', '#e3edf4', '#778b9a'][i % 4]}" stroke="#f8ffff" stroke-width=".7"/>`;
        }
        facets += '<path d="M34 28L65 28L77 49L65 72L34 72L23 49Z" fill="#e4eff7" stroke="#fff" stroke-width="1.5"/><path d="M34 28L65 72M65 28L34 72M23 49H77" stroke="#a0b3c0" fill="none"/>';
      }
      gem.innerHTML = `<defs><clipPath id="${uid}"><path d="${outline}"/></clipPath></defs><g clip-path="url(#${uid})">${facets}</g><path d="${outline}" fill="none" stroke="#fff" stroke-width="1.5"/>`;

      // Spec line
      const carat = this.dataset.carat || fields.carat || '';
      this.querySelector('.hs-diamond-hand__spec').textContent =
        `${shape}${carat ? ' · ' + carat + ' ct' : ''} · ${length.toFixed(2)} × ${width.toFixed(2)} mm`;
      this.dataset.resolvedShape = shape;
      this.dataset.widthMm = width;
      this.dataset.lengthMm = length;

      // Smooth skin-tone slider
      const slider = this.querySelector('input');
      const output = this.querySelector('output');

      const update = () => {
        const n = clamp(Number(slider.value));
        const i = Math.min(MAX - 1, Math.floor(n));
        const t = n - i;
        const a = tones[i], b = tones[i + 1];
        this.style.setProperty('--hs-skin-color', mixColor(a[0], b[0], t));
        this.style.setProperty('--hs-skin-opacity', (a[1] + (b[1] - a[1]) * t).toFixed(3));
        const label = Math.round(n) + 1;
        output.textContent = `${label} of 6`;
        slider.setAttribute('aria-valuetext', `Skin tone ${label} of 6`);
      };

      slider.addEventListener('input', update);

      // Arrow keys jump one full tone instead of 0.01
      slider.addEventListener('keydown', e => {
        const dir = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
        if (!dir) return;
        e.preventDefault();
        slider.value = clamp(Math.round(Number(slider.value)) + dir);
        update();
      });

      update();
      this.hidden = false;
    }
  });
})();
